import React, { useState, useEffect } from 'react'
import styles from './CollectionReportDownloadOptions.module.css'
import * as XLSX from "xlsx";
import { convertToAmPm } from '../../Helper/calculations'

const CollectionReportDownloadOptions = (props) => {
    const { isOpen, collectionReport, isGstAvailable, dates, includeServiceItems, includeExpenses, onClose } = props

    const availableColumns = [
        { id: 'billNo', label: 'BILL No', enabled: true },
        { id: 'date', label: 'DATE', enabled: true },
        { id: 'time', label: 'TIME', enabled: false },
        { id: 'name', label: 'NAME', enabled: false },
        { id: 'mobile', label: 'MOBILE', enabled: false },
        { id: 'taxableAmount', label: 'TAXABLE AMOUNT', enabled: true },
        { id: 'cgst', label: 'CGST', enabled: true },
        { id: 'sgst', label: 'SGST', enabled: true },
        { id: 'igst', label: 'IGST', enabled: true },
        { id: 'totalAmount', label: 'TOTAL AMOUNT', enabled: true },
        { id: 'viaCash', label: 'VIA CASH', enabled: false },
        { id: 'viaCard', label: 'VIA CARD', enabled: false },
        { id: 'viaQr', label: 'VIA QR', enabled: false },
        { id: 'gst', label: 'GST', enabled: isGstAvailable }
    ];

    const [columns, setColumns] = useState(availableColumns);
    const [gstRate, setGstRate] = useState(5);
    const [draggedIndex, setDraggedIndex] = useState(null);

    useEffect(() => {
        if (isOpen) {
            setColumns(availableColumns);
            setGstRate(5);
        }
    }, [isOpen]);

    const toggleColumn = (id) => {
        setColumns(prev => prev.map(col =>
            col.id === id ? { ...col, enabled: !col.enabled } : col
        ));
    };

    const handleDragStart = (e, index) => {
        setDraggedIndex(index);
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleDragOver = (e, index) => {
        e.preventDefault();
        if (draggedIndex === null || draggedIndex === index) return;

        const newColumns = [...columns];
        const draggedItem = newColumns[draggedIndex];
        newColumns.splice(draggedIndex, 1);
        newColumns.splice(index, 0, draggedItem);

        setColumns(newColumns);
        setDraggedIndex(index);
    };

    const handleDragEnd = () => {
        setDraggedIndex(null);
    };

    const moveColumn = (index, direction) => {
        if (
            (direction === 'up' && index === 0) ||
            (direction === 'down' && index === columns.length - 1)
        ) return;

        const newColumns = [...columns];
        const targetIndex = direction === 'up' ? index - 1 : index + 1;
        [newColumns[index], newColumns[targetIndex]] =
            [newColumns[targetIndex], newColumns[index]];
        setColumns(newColumns);
    };

    const getColumnValue = (item, columnId) => {
        switch (columnId) {
            case 'billNo': return `${item?.invoiceNumber?.substr(18, 9)}`;
            case 'date': return item?.payStatusLastUpdatedAt?.substr(0, 10) || "";
            case 'time': return item?.payStatusLastUpdatedAt
                ? convertToAmPm(item?.payStatusLastUpdatedAt.substr(11, 8), false)
                : "";
            case 'name': return item?.clientName || "";
            case 'mobile': return item?.clientPhoneNumber || "";
            case 'taxableAmount':
                return Number(parseFloat(parseFloat(item?.amountToBeCollected * 100) / (100 + gstRate)).toFixed(2) || 0);
            case 'cgst':
                return Number(parseFloat(item?.amountToBeCollected * (parseFloat(gstRate / 2).toFixed(2) / (100 + gstRate))).toFixed(2) || 0);
            case 'sgst':
                return Number(parseFloat(item?.amountToBeCollected * (parseFloat(gstRate / 2).toFixed(2) / (100 + gstRate))).toFixed(2) || 0);
            case 'igst':
                return Number(0);
            case 'totalAmount':
                return Number(item?.amountToBeCollected || 0);
            case 'viaCash':
                return Number(item?.collectionViaCash || 0);
            case 'viaCard':
                return Number(item?.collectionViaCard || 0);
            case 'viaQr':
                return Number(item?.collectionViaQR || 0);
            case 'gst':
                return Number(parseFloat(parseFloat(item?.amountToBeCollected) * (gstRate / (100 + gstRate))).toFixed(2) || 0);
            default:
                return "";
        }
    };

    const downloadExcelForGst = () => {
        if (!collectionReport) {
            alert("No data available");
            return;
        }

        const workbook = XLSX.utils.book_new();
        const sheetData = [];

        const enabledColumns = columns.filter(col => col.enabled);
        const header = enabledColumns.map(col => col.label);
        sheetData.push(header);

        const transactions = collectionReport?.allTransactions || [];

        transactions.forEach(item => {
            const mainRow = enabledColumns.map(col => getColumnValue(item, col.id));
            sheetData.push(mainRow);

            if (includeServiceItems && item?.allItemsEachBooking?.length) {
                item.allItemsEachBooking.forEach(cart => {
                    const subRow = enabledColumns.map(col => {
                        if (col.id === 'billNo') return `   ${cart.serviceName}`;
                        if (col.id === 'taxableAmount') return Number(cart.totalDiscPrice || 0);
                        return "";
                    });
                    sheetData.push(subRow);
                });
            }
        });

        const sheet1 = XLSX.utils.aoa_to_sheet(sheetData);
        XLSX.utils.book_append_sheet(workbook, sheet1, "Transactions");

        const gstCalculated = Number(parseFloat(collectionReport?.totalCollection * (gstRate / (100 + gstRate))).toFixed(2));

        const summarySheet = XLSX.utils.aoa_to_sheet([
            ["Summary"],
            [],
            ["Total Sales", collectionReport.totalCollection],
            [`Final GST (${gstRate}% rate)`, gstCalculated],
            ["Total Normal Collection", collectionReport.collectionViaCard + collectionReport.collectionViaCash + collectionReport.collectionViaQr],
            ["Total Older Collection", collectionReport.olderPayTotalAmount],
            ["Total Refund", 0],
            ["Total Expenses", collectionReport.totalExpense],
            ["Final Collection", collectionReport.totalCollection - (includeExpenses ? collectionReport.totalExpense : 0)]
        ]);

        XLSX.utils.book_append_sheet(workbook, summarySheet, "Summary");

        XLSX.writeFile(
            workbook,
            `collection_report_${dates.startDate}_to_${dates.endDate}.xlsx`
        );
    };

    if (!isOpen) return null;

    return (
        <div className={styles.mainWrapper}>
            <div className={styles.mainContainer}>
                <div className={styles.mainContent}>
                    <div className={styles.container}>
                        <div className={styles.headerWrapper} >
                            <div className={styles.title}>Download your gst compatible report.</div>
                        </div>

                        <div className={styles.bodyWrapper}>
                            {/* GST Rate Input */}
                            <div className={styles.gstSection}>
                                <label className={styles.gstLabel}>GST Rate (%)</label>

                                <input
                                    type="number"
                                    value={gstRate}
                                    onChange={(e) => setGstRate(parseFloat(e.target.value) || 0)}
                                    className={styles.gstInput}
                                    min="0"
                                    max="100"
                                    step="0.1"
                                />
                            </div>

                            {/* Column Selection */}
                            <div className={styles.columnsSection}>
                                <h3 className={styles.sectionTitle}>Select & Reorder Columns</h3>
                                <p className={styles.hint}>Drag to reorder, toggle to include/exclude</p>

                                <div className={styles.columnsList}>
                                    {columns.map((col, index) => (
                                        <div
                                            key={col.id}
                                            draggable
                                            onDragStart={(e) => handleDragStart(e, index)}
                                            onDragOver={(e) => handleDragOver(e, index)}
                                            onDragEnd={handleDragEnd}
                                            className={`${styles.columnItem} ${col.enabled ? styles.columnItemEnabled : styles.columnItemDisabled}`}
                                            style={{ opacity: draggedIndex === index ? 0.5 : 1 }}
                                        >
                                            <div className={styles.columnLeft}>
                                                <span className={styles.dragHandle}>⋮⋮</span>
                                                <span className={styles.columnLabel}>{col.label}</span>
                                            </div>

                                            <div className={styles.columnRight}>
                                                <button
                                                    onClick={() => moveColumn(index, 'up')}
                                                    disabled={index === 0}
                                                    className={styles.arrowBtn}
                                                >↑</button>

                                                <button
                                                    onClick={() => moveColumn(index, 'down')}
                                                    disabled={index === columns.length - 1}
                                                    className={styles.arrowBtn}
                                                >↓</button>

                                                <label className={styles.toggle}>
                                                    <input
                                                        type="checkbox"
                                                        checked={col.enabled}
                                                        onChange={() => toggleColumn(col.id)}
                                                        className={styles.checkbox}
                                                    />

                                                    <span className={`${styles.toggleSlider} ${col.enabled ? styles.toggleActive : ''}`}></span>
                                                </label>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div className={styles.actions}>
                            <button
                                onClick={() => props.onClose?.()}
                                className={styles.cancelBtn}
                            >
                                Cancel
                            </button>

                            <button
                                onClick={downloadExcelForGst}
                                className={styles.downloadBtn}
                                disabled={!columns.some(col => col.enabled)}
                            >
                                Download Excel
                            </button>
                        </div>

                    </div>
                </div>
            </div>
        </div>
    );
};

export default CollectionReportDownloadOptions;
